/* ============================================================
   useScrollArrows — flechas de scroll horizontal para una fila
   con overflow. Equivale al makeScroller del HTML.

   Uso:
     const track = useRef(null);
     const { showArrows, canPrev, canNext, scrollPrev, scrollNext, recheck } = useScrollArrows(track);

     <button hidden={!showArrows} disabled={!canPrev} onClick={scrollPrev}>‹</button>
     <div ref={track} className="...">{children}</div>
     <button hidden={!showArrows} disabled={!canNext} onClick={scrollNext}>›</button>

   Llamar recheck() cuando cambie el contenido o el layout
   (abrir el panel, cambiar dock/modal, cargar la fuente).
   ============================================================ */
import { useCallback, useEffect, useState } from 'react';

const EPS = 2; // tolerancia de subpíxel

export default function useScrollArrows(ref) {
    const [state, setState] = useState({ showArrows: false, canPrev: false, canNext: false });

    const recheck = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;      // desplazamiento máximo posible
        const overflow = max > EPS;
        setState({
            showArrows: overflow,
            canPrev: overflow && el.scrollLeft > EPS,
            canNext: overflow && el.scrollLeft < max - EPS,
        });
    }, [ref]);

    const scrollBy = useCallback((dir) => {
        const el = ref.current;
        if (!el) return;
        el.scrollBy({ left: dir * Math.max(120, el.clientWidth * 0.6), behavior: 'smooth' });
        // el scroll suave puede tardar en emitir 'scroll'; forzamos un recheck al terminar
        setTimeout(recheck, 320);
    }, [ref, recheck]);

    const scrollPrev = useCallback(() => scrollBy(-1), [scrollBy]);
    const scrollNext = useCallback(() => scrollBy(1), [scrollBy]);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        // primer cálculo tras el layout (evita medir con anchos en 0)
        const raf = requestAnimationFrame(recheck);

        el.addEventListener('scroll', recheck, { passive: true });
        window.addEventListener('resize', recheck);

        // ResizeObserver: recalcula si cambia el ancho del track o de su contenido
        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(recheck);
            ro.observe(el);
        }

        if (document.fonts && document.fonts.ready) document.fonts.ready.then(recheck);

        return () => {
            cancelAnimationFrame(raf);
            el.removeEventListener('scroll', recheck);
            window.removeEventListener('resize', recheck);
            if (ro) ro.disconnect();
        };
    }, [ref, recheck]);

    return { ...state, scrollPrev, scrollNext, recheck };
}